/**
 * Admin — Module registry
 *
 * The GLOBAL module catalog and the platform policy over it. This is the
 * registry half of the registry-vs-channel split: not "is this module on for
 * my channel" (that's pages/Modules.tsx, per channel) but "who may turn it on
 * at all, is it globally killed, and what settings baseline do they inherit".
 *
 * Super-admin only, routed at /admin/modules. Before this page existed that
 * route rendered the per-channel <Modules/> page, which needs a channelId and
 * misbehaved without one.
 *
 * Two API facts shape the whole page:
 *
 *   - Every mutating registry route returns the FULL refreshed RegistryEntry,
 *     so each write replaces its row from the response. No optimistic state,
 *     no refetch — the row on screen is always what the server just stored.
 *   - Reload lives on the per-channel router (/api/modules/{name}/reload) and
 *     404s unless the module is currently LOADED. So the control is gated on
 *     entry.loaded; a module that failed to import needs a service restart,
 *     not a reload, and offering the button would only produce a 404.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import adminModulesApi, {
  AVAILABILITY_OPTIONS,
  type AdminUser,
  type GlobalSettingsUpdate,
  type ModuleAvailability,
  type RegistryEntry,
} from '@/api/adminModulesApi'
import { apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  Save,
  SlidersHorizontal,
  Users,
  X,
} from 'lucide-react'
import clsx from 'clsx'

export default function AdminModules() {
  const [entries, setEntries] = useState<RegistryEntry[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [usersUnavailable, setUsersUnavailable] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const { success, error } = useToast()

  /**
   * Catalog and user list load independently.
   *
   * A failed user list must not take the page down: existing grants already
   * carry their usernames in granted_users, so only the "add a user" picker
   * degrades. Losing the catalog, by contrast, leaves nothing to render.
   */
  const load = useCallback(async () => {
    const [catalog, userList] = await Promise.allSettled([
      adminModulesApi.list(),
      adminModulesApi.listUsers(),
    ])

    if (catalog.status === 'fulfilled') {
      setEntries(catalog.value)
      setLoadError(null)
    } else {
      setLoadError(
        apiErrorMessage(catalog.reason, 'Could not load the module registry.'),
      )
    }

    if (userList.status === 'fulfilled') {
      setUsers(userList.value)
      setUsersUnavailable(false)
    } else {
      setUsers([])
      setUsersUnavailable(true)
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const replaceEntry = useCallback((updated: RegistryEntry) => {
    setEntries((prev) =>
      prev.map((e) => (e.name === updated.name ? updated : e)),
    )
  }, [])

  /** Every registry write goes through here: busy flag, row swap, toast. */
  const mutate = useCallback(
    async (
      name: string,
      call: () => Promise<RegistryEntry>,
      successMessage: string,
      failureMessage: string,
    ) => {
      setBusy(name)
      try {
        replaceEntry(await call())
        success(successMessage)
      } catch (err) {
        error(apiErrorMessage(err, failureMessage))
      } finally {
        setBusy(null)
      }
    },
    [replaceEntry, success, error],
  )

  const handleAvailability = (entry: RegistryEntry, value: ModuleAvailability) => {
    const label =
      AVAILABILITY_OPTIONS.find((o) => o.value === value)?.label ?? value
    mutate(
      entry.name,
      () => adminModulesApi.patch(entry.name, { availability: value }),
      `${entry.display_name} is now available to: ${label}.`,
      `Could not change availability for ${entry.display_name}.`,
    )
  }

  const handleToggleEnabled = (entry: RegistryEntry) => {
    const turningOff = entry.enabled

    // The kill switch is global. Turning it off doesn't disable the channel
    // rows, it overrules them — so the count is what an admin needs to see.
    if (turningOff && entry.enabled_channel_count > 0) {
      const where =
        entry.enabled_channel_count === 1
          ? '1 channel currently has it enabled'
          : `${entry.enabled_channel_count} channels currently have it enabled`
      if (
        !confirm(
          `Globally disable ${entry.display_name}?\n\n${where}, and will stop being able to use it. Their per-channel settings are kept, so re-enabling here restores them.`,
        )
      ) {
        return
      }
    }

    mutate(
      entry.name,
      () => adminModulesApi.patch(entry.name, { enabled: !entry.enabled }),
      turningOff
        ? `${entry.display_name} is globally disabled.`
        : `${entry.display_name} is globally enabled.`,
      `Could not change the kill switch for ${entry.display_name}.`,
    )
  }

  /** Reload returns a result, not an entry — so it bypasses mutate(). */
  const handleReload = async (entry: RegistryEntry) => {
    setBusy(entry.name)
    try {
      const result = await adminModulesApi.reload(entry.name)
      success(
        result.version
          ? `${entry.display_name} reloaded (v${result.version}).`
          : `${entry.display_name} reloaded.`,
      )
      // Version and capability metadata can change across a reload.
      replaceEntry(await adminModulesApi.get(entry.name))
    } catch (err) {
      error(apiErrorMessage(err, `Could not reload ${entry.display_name}.`))
    } finally {
      setBusy(null)
    }
  }

  const handleGrant = (entry: RegistryEntry, userId: number) => {
    const who = users.find((u) => u.id === userId)?.twitch_username ?? 'User'
    mutate(
      entry.name,
      () => adminModulesApi.grantAccess(entry.name, userId),
      `${who} can now enable ${entry.display_name}.`,
      `Could not grant access to ${entry.display_name}.`,
    )
  }

  const handleRevoke = (entry: RegistryEntry, userId: number, who: string) => {
    mutate(
      entry.name,
      () => adminModulesApi.revokeAccess(entry.name, userId),
      `Revoked ${who}'s access to ${entry.display_name}.`,
      `Could not revoke access to ${entry.display_name}.`,
    )
  }

  const handleSaveSettings = (
    entry: RegistryEntry,
    data: GlobalSettingsUpdate,
  ) =>
    mutate(
      entry.name,
      () => adminModulesApi.setGlobalSettings(entry.name, data),
      `Global settings saved for ${entry.display_name}.`,
      `Could not save global settings for ${entry.display_name}.`,
    )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <SlidersHorizontal size={24} className="text-synth-magenta" />
          Module registry
        </h1>
        <p className="text-text-secondary text-sm mt-1">
          Platform policy for every module: who may enable it, whether it's
          globally available, and the settings baseline channels inherit.
          Enabling a module <em>for a channel</em> happens on that channel's own
          Modules page.
        </p>
      </div>

      {loadError && (
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="text-state-danger shrink-0" size={18} />
          <span className="text-state-danger text-sm">{loadError}</span>
        </div>
      )}

      {usersUnavailable && (
        <div className="bg-state-warning-bg border border-state-warning rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="text-state-warning shrink-0 mt-0.5" size={18} />
          <span className="text-state-warning text-sm">
            Couldn't load the user list, so new access grants can't be added
            right now. Existing grants are still shown and can be revoked.
          </span>
        </div>
      )}

      {entries.length === 0 && !loadError ? (
        <div className="card">
          <p className="text-text-muted text-sm py-6 text-center">
            No modules found. The registry is seeded at startup from the modules
            discovered on disk.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <ModuleCard
              key={entry.name}
              entry={entry}
              users={users}
              usersUnavailable={usersUnavailable}
              busy={busy === entry.name}
              isExpanded={expanded === entry.name}
              onToggleExpand={() =>
                setExpanded((prev) => (prev === entry.name ? null : entry.name))
              }
              onAvailability={(v) => handleAvailability(entry, v)}
              onToggleEnabled={() => handleToggleEnabled(entry)}
              onReload={() => handleReload(entry)}
              onGrant={(userId) => handleGrant(entry, userId)}
              onRevoke={(userId, who) => handleRevoke(entry, userId, who)}
              onSaveSettings={(data) => handleSaveSettings(entry, data)}
            />
          ))}
        </div>
      )}

      <p className="text-text-muted text-xs leading-relaxed">
        Availability and the kill switch are enforced when a user enables a
        module for a channel, and are never overwritten by the startup registry
        sync &mdash; a tier set here survives restarts. Note that in the
        per-channel catalog a super admin impersonating another user may see
        their <em>own</em> access rather than the impersonated user's; that's a
        display quirk only, as the enable call itself always enforces against
        the effective user.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* One module                                                          */
/* ------------------------------------------------------------------ */

function ModuleCard({
  entry,
  users,
  usersUnavailable,
  busy,
  isExpanded,
  onToggleExpand,
  onAvailability,
  onToggleEnabled,
  onReload,
  onGrant,
  onRevoke,
  onSaveSettings,
}: {
  entry: RegistryEntry
  users: AdminUser[]
  usersUnavailable: boolean
  busy: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onAvailability: (value: ModuleAvailability) => void
  onToggleEnabled: () => void
  onReload: () => void
  onGrant: (userId: number) => void
  onRevoke: (userId: number, who: string) => void
  onSaveSettings: (data: GlobalSettingsUpdate) => void
}) {
  const availabilityHelp = AVAILABILITY_OPTIONS.find(
    (o) => o.value === entry.availability,
  )?.help

  return (
    <div
      className={clsx(
        'card card-pad-sm',
        !entry.enabled && 'border-state-danger/40',
      )}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          className="mt-0.5 p-1 -m-1 rounded text-text-muted hover:text-text-primary shrink-0"
          aria-label={
            isExpanded
              ? `Collapse ${entry.display_name}`
              : `Expand ${entry.display_name}`
          }
        >
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-primary font-medium">
              {entry.display_name}
            </span>
            {entry.version && (
              <span className="code-inline text-xs">v{entry.version}</span>
            )}
            <StatusBadges entry={entry} />
          </div>

          <p className="text-text-muted text-xs mt-1">
            <span className="code-inline">{entry.name}</span>
            {entry.description ? ` — ${entry.description}` : ''}
          </p>

          <p className="text-text-muted text-xs mt-1">
            Enabled on{' '}
            <span className="text-text-secondary tabular-nums">
              {entry.enabled_channel_count}
            </span>{' '}
            {entry.enabled_channel_count === 1 ? 'channel' : 'channels'}
            {entry.granted_users.length > 0 && (
              <>
                {' · '}
                {entry.granted_users.length}{' '}
                {entry.granted_users.length === 1 ? 'grant' : 'grants'}
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {busy && (
            <Loader2 className="animate-spin text-text-secondary" size={18} />
          )}

          {/* Reload 404s on an unloaded module — no button rather than a
              button that always fails. */}
          {entry.loaded && (
            <button
              onClick={onReload}
              disabled={busy}
              className="btn btn-ghost btn-sm disabled:opacity-50"
              title="Hot-reload this module for every channel"
            >
              <RefreshCw size={15} className="mr-1.5" />
              Reload
            </button>
          )}

          <button
            onClick={onToggleEnabled}
            disabled={busy}
            className={clsx(
              'btn btn-sm disabled:opacity-50',
              entry.enabled ? 'btn-secondary' : 'btn-danger',
            )}
            title={
              entry.enabled
                ? 'Globally disable this module'
                : 'Globally enable this module'
            }
          >
            <Power size={15} className="mr-1.5" />
            {entry.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {/* Availability */}
      <div className="mt-3 pl-8 flex flex-wrap items-center gap-3">
        <label
          className="label mb-0 text-xs"
          htmlFor={`availability-${entry.name}`}
        >
          Available to
        </label>
        <select
          id={`availability-${entry.name}`}
          className="select w-auto text-sm py-1.5"
          value={entry.availability}
          disabled={busy}
          onChange={(e) =>
            onAvailability(e.target.value as ModuleAvailability)
          }
        >
          {AVAILABILITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {availabilityHelp && (
          <span className="text-text-muted text-xs">{availabilityHelp}</span>
        )}
      </div>

      {isExpanded && (
        <div className="mt-4 pl-8 space-y-5">
          <div className="divider" />

          <GrantsEditor
            entry={entry}
            users={users}
            usersUnavailable={usersUnavailable}
            busy={busy}
            onGrant={onGrant}
            onRevoke={onRevoke}
          />

          <div className="divider" />

          <GlobalSettingsEditor
            key={entry.name}
            entry={entry}
            busy={busy}
            onSave={onSaveSettings}
          />

          {(entry.provides_capabilities.length > 0 ||
            entry.consumes_capabilities.length > 0) && (
            <>
              <div className="divider" />
              <CapabilityList entry={entry} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function StatusBadges({ entry }: { entry: RegistryEntry }) {
  return (
    <>
      {!entry.enabled && <span className="badge-danger">Globally off</span>}
      {entry.loaded ? (
        <span className="badge-success">Loaded</span>
      ) : entry.discovered ? (
        <span className="badge-danger">Failed to load</span>
      ) : (
        <span className="badge-neutral">Not on disk</span>
      )}
    </>
  )
}

/**
 * Declared capability metadata.
 *
 * Since R20 chat 2, "Provides" is enforced at load — an undeclared registration
 * is refused — and "Consumes" stays advisory. Not a permission manifest: R6
 * established that custom-HTML author code is scoped by the sandbox harness,
 * not by module metadata (architecture/module-platform.md section C).
 */
function CapabilityList({ entry }: { entry: RegistryEntry }) {
  return (
    <div>
      <p className="label mb-2">Capabilities</p>
      <div className="space-y-2">
        {entry.provides_capabilities.length > 0 && (
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-text-muted text-xs mt-0.5 w-20 shrink-0">
              Provides
            </span>
            {entry.provides_capabilities.map((cap) => (
              <span key={cap} className="badge-info">
                {cap}
              </span>
            ))}
          </div>
        )}
        {entry.consumes_capabilities.length > 0 && (
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-text-muted text-xs mt-0.5 w-20 shrink-0">
              Consumes
            </span>
            {entry.consumes_capabilities.map((cap) => (
              <span key={cap} className="badge-neutral">
                {cap}
              </span>
            ))}
          </div>
        )}
      </div>
      <p className="help-text">
        Declared by the module. &ldquo;Provides&rdquo; is enforced when the
        module loads &mdash; it cannot publish anything it did not declare.
        &ldquo;Consumes&rdquo; is informational.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Per-user access grants                                              */
/* ------------------------------------------------------------------ */

function GrantsEditor({
  entry,
  users,
  usersUnavailable,
  busy,
  onGrant,
  onRevoke,
}: {
  entry: RegistryEntry
  users: AdminUser[]
  usersUnavailable: boolean
  busy: boolean
  onGrant: (userId: number) => void
  onRevoke: (userId: number, who: string) => void
}) {
  const [selected, setSelected] = useState<string>('')

  const grantedIds = useMemo(
    () => new Set(entry.granted_users.map((u) => u.id)),
    [entry.granted_users],
  )
  const candidates = useMemo(
    () => users.filter((u) => !grantedIds.has(u.id)),
    [users, grantedIds],
  )

  const inert = entry.availability !== 'specific_users'

  return (
    <div>
      <p className="label mb-2 flex items-center gap-2">
        <Users size={15} className="text-text-muted" />
        Access grants
      </p>

      {inert && (
        <p className="help-text mb-2 mt-0">
          Stored but not consulted while availability is &ldquo;
          {AVAILABILITY_OPTIONS.find((o) => o.value === entry.availability)
            ?.label ?? entry.availability}
          &rdquo;. Grants take effect when the tier is set to Specific users.
        </p>
      )}

      {entry.granted_users.length === 0 ? (
        <p className="text-text-muted text-sm">No users granted access.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {entry.granted_users.map((user) => (
            <span
              key={user.id}
              className="badge-neutral gap-1.5 pr-1 py-1"
              title={`Internal user id ${user.id}`}
            >
              {user.twitch_username}
              <button
                onClick={() => onRevoke(user.id, user.twitch_username)}
                disabled={busy}
                aria-label={`Revoke ${user.twitch_username}`}
                className="p-0.5 rounded text-text-muted hover:text-state-danger disabled:opacity-50"
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      {!usersUnavailable && (
        <div className="flex items-center gap-2 mt-3">
          <select
            className="select w-auto text-sm py-1.5"
            value={selected}
            disabled={busy || candidates.length === 0}
            onChange={(e) => setSelected(e.target.value)}
            aria-label={`Add a user to ${entry.display_name}`}
          >
            <option value="">
              {candidates.length === 0
                ? 'Every user already granted'
                : 'Select a user\u2026'}
            </option>
            {candidates.map((user) => (
              <option key={user.id} value={String(user.id)}>
                {user.twitch_username}
                {user.is_super_admin ? ' (admin)' : ''}
                {user.is_active ? '' : ' (inactive)'}
              </option>
            ))}
          </select>
          <button
            className="btn btn-secondary btn-sm disabled:opacity-50"
            disabled={busy || !selected}
            onClick={() => {
              if (!selected) return
              onGrant(Number(selected))
              setSelected('')
            }}
          >
            <Plus size={15} className="mr-1.5" />
            Grant
          </button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Global settings + overridable keys                                  */
/* ------------------------------------------------------------------ */

interface ParseResult {
  ok: boolean
  value: Record<string, unknown>
  message?: string
}

function parseSettings(text: string): ParseResult {
  const trimmed = text.trim()
  if (!trimmed) return { ok: true, value: {} }
  try {
    const value: unknown = JSON.parse(trimmed)
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return {
        ok: false,
        value: {},
        message: 'Settings must be a JSON object, not an array or a bare value.',
      }
    }
    return { ok: true, value: value as Record<string, unknown> }
  } catch (err) {
    return {
      ok: false,
      value: {},
      message: err instanceof Error ? err.message : 'Invalid JSON.',
    }
  }
}

/**
 * The admin settings baseline and the keys a channel owner may override.
 *
 * A JSON textarea rather than generated form controls: settings are free-form
 * JSONB and there's no admin-side endpoint that enumerates a module's legal
 * keys (the schema route is per-channel), so any structured editor would be
 * guessing. Matches the design system's JsonField, which is a validated
 * textarea by decision — no CodeMirror dependency.
 *
 * The overridable-key list is NOT limited to keys present in the JSON: a key
 * can live in the module's own get_default_settings() and never appear here,
 * yet still be a legitimate thing to let a channel override. Hence the free
 * "add key" input alongside the checkboxes.
 */
function GlobalSettingsEditor({
  entry,
  busy,
  onSave,
}: {
  entry: RegistryEntry
  busy: boolean
  onSave: (data: GlobalSettingsUpdate) => void
}) {
  const [draft, setDraft] = useState(() =>
    JSON.stringify(entry.global_settings ?? {}, null, 2),
  )
  const [keys, setKeys] = useState<string[]>(entry.user_overridable_keys)
  const [newKey, setNewKey] = useState('')

  const parsed = useMemo(() => parseSettings(draft), [draft])

  // Keys from the JSON, plus any already-selected key that isn't in it.
  const keyUniverse = useMemo(() => {
    const fromJson = parsed.ok ? Object.keys(parsed.value) : []
    return Array.from(new Set([...fromJson, ...keys])).sort()
  }, [parsed, keys])

  const toggleKey = (key: string) =>
    setKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )

  const addKey = () => {
    const key = newKey.trim()
    if (!key || keys.includes(key)) {
      setNewKey('')
      return
    }
    setKeys((prev) => [...prev, key])
    setNewKey('')
  }

  return (
    <div>
      <p className="label mb-2">Global settings</p>

      <p className="help-text mt-0 mb-2">
        The admin-controlled baseline. Channels inherit these and may only
        change the keys ticked below; everything else is locked and a channel
        writing to it is refused. Note that saving this the first time is what
        starts locking &mdash; until a row exists, channels can override any
        key.
      </p>

      <textarea
        className="textarea font-mono text-[13px] min-h-[9rem]"
        spellCheck={false}
        value={draft}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        aria-invalid={!parsed.ok}
        aria-label={`Global settings JSON for ${entry.display_name}`}
      />
      {parsed.ok ? (
        <p className="help-text">
          A JSON object. Saving replaces the stored settings entirely &mdash;
          this is not a merge, so a key you remove here is deleted.
        </p>
      ) : (
        <p className="error-text">{parsed.message}</p>
      )}

      <p className="label mt-4 mb-2">Channel-overridable keys</p>
      {keyUniverse.length === 0 ? (
        <p className="text-text-muted text-sm">
          No keys yet. Add settings above, or name a key from the module's own
          defaults below.
        </p>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {keyUniverse.map((key) => {
            const inJson = parsed.ok && key in parsed.value
            return (
              <label
                key={key}
                className="flex items-center gap-2 text-sm text-text-secondary"
              >
                <input
                  type="checkbox"
                  checked={keys.includes(key)}
                  disabled={busy}
                  onChange={() => toggleKey(key)}
                  className="accent-synth-magenta"
                />
                <span className="font-mono text-[13px]">{key}</span>
                {!inJson && (
                  <span
                    className="text-text-muted text-xs"
                    title="Not in the settings above — presumably a module default"
                  >
                    (module default)
                  </span>
                )}
              </label>
            )
          })}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <input
          className="input w-auto text-sm py-1.5 font-mono"
          placeholder="add a key by name"
          value={newKey}
          disabled={busy}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addKey()
            }
          }}
          aria-label="Add an overridable key by name"
        />
        <button
          className="btn btn-ghost btn-sm disabled:opacity-50"
          disabled={busy || !newKey.trim()}
          onClick={addKey}
        >
          <Plus size={15} className="mr-1.5" />
          Add key
        </button>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          className="btn btn-primary btn-sm disabled:opacity-50"
          disabled={busy || !parsed.ok}
          onClick={() =>
            onSave({ settings: parsed.value, user_overridable_keys: keys })
          }
        >
          <Save size={15} className="mr-1.5" />
          Save global settings
        </button>
        {!entry.has_global_settings && (
          <span className="text-text-muted text-xs">
            No settings row exists yet for this module.
          </span>
        )}
      </div>
    </div>
  )
}
